/**
 * What this device IS — an explicit configuration, not a hardware check.
 *
 * The mode gate used to be `isTouchDevice()`: "running Capacitor, and (a URL
 * param or a stored panel token)". That is a statement about *hardware*, and it
 * is the wrong predicate for a product claim as broad as "any app can be
 * configured as a panel" (POSITIONING.md, Consequence 4): a Windows install can
 * run a panel, a tablet can run a panel beside its normal UI, and a phone that
 * merely has a panel token in storage is not a wall panel. What decides is what
 * the *owner* configured this device to be.
 *
 * Two modes, and one decision function:
 *
 *   - `takeover` — this device is a panel: the panel surface is the window, the
 *     kiosk routing applies, and the panel credential is the window's credential
 *     on the routes that accept one. Unchanged behaviour for wall panels.
 *   - `app` — this device is an app. It may still *open* a panel surface, as a
 *     miniapp beside its normal UI (`stores/panelApp.ts`), with its own
 *     credential.
 *
 * Precedence, highest first — the same shape as the credential rule, and for the
 * same reason (a stale flag must never decide what a device is):
 *
 *   1. the explicit configuration (`otto_panel_device_mode`);
 *   2. the URL asking for a panel (`?mode=panel`, `?panel=1`) — a one-shot
 *      instruction, honored as `takeover` on a native install (a wall panel is
 *      launched with it) and as `app` everywhere else (a desktop asked to show a
 *      panel surface gets the miniapp, not its window taken over);
 *   3. a native install that is already paired as a panel, from before this
 *      setting existed — kept as `takeover`, and back-filled into the explicit
 *      configuration on the next start (`backfillPanelDeviceConfiguration()`), so
 *      one behaviour change is never silently applied to a wall panel in the field;
 *   4. **the safe default: `app`.** A device nobody has configured is an app that
 *      can open a panel, never a device that has become one.
 */
import { isCapacitor } from './runtime';

export type PanelDeviceMode = 'takeover' | 'app';

/** Why this device has the mode it has — carried into logs and handoffs. */
export type PanelDeviceSource = 'configured' | 'url' | 'legacy-native-paired' | 'default';

export const PANEL_DEVICE_MODE_KEY = 'otto_panel_device_mode';

/** What the mode was read from. Injectable so the resolver is testable. */
export interface PanelDeviceStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function safeStorage(): PanelDeviceStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** The explicit configuration, or null when nobody has configured this device. */
export function readPanelDeviceMode(storage: PanelDeviceStorage | null = safeStorage()): PanelDeviceMode | null {
  try {
    const value = storage?.getItem(PANEL_DEVICE_MODE_KEY);
    return value === 'takeover' || value === 'app' ? value : null;
  } catch {
    return null;
  }
}

/** Configure this device (or clear the configuration with null). */
export function writePanelDeviceMode(
  mode: PanelDeviceMode | null,
  storage: PanelDeviceStorage | null = safeStorage(),
): void {
  try {
    if (mode === null) storage?.removeItem(PANEL_DEVICE_MODE_KEY);
    else storage?.setItem(PANEL_DEVICE_MODE_KEY, mode);
  } catch {
    // Storage unavailable: the runtime still holds the in-memory decision.
  }
}

/** The panel credential a paired device must have in storage to be a wall panel. */
export const PANEL_CREDENTIAL_STORAGE_KEY = 'touch_device_token';

export function readStoredPanelCredential(storage: PanelDeviceStorage | null = safeStorage()): string | null {
  try {
    const value = storage?.getItem(PANEL_CREDENTIAL_STORAGE_KEY);
    return value && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

// ─── The decision ────────────────────────────────────────────────────────────

export interface PanelDeviceFacts {
  /** The persisted explicit configuration, if any. */
  configured: string | null;
  /** `?mode=panel` / `?panel=1` on the current URL. */
  urlParamPanel: boolean;
  /** A native (Capacitor) install: the wall-panel build, and the only one that ever had the legacy gate. */
  nativeApp: boolean;
  /** A paired panel credential is present on this device. */
  hasPanelCredential: boolean;
}

export interface PanelDeviceDecision {
  mode: PanelDeviceMode;
  source: PanelDeviceSource;
}

export function resolvePanelDeviceMode(facts: PanelDeviceFacts): PanelDeviceDecision {
  if (facts.configured === 'takeover' || facts.configured === 'app') {
    return { mode: facts.configured, source: 'configured' };
  }

  if (facts.urlParamPanel) {
    return { mode: facts.nativeApp ? 'takeover' : 'app', source: 'url' };
  }

  // A wall panel already in the field: the native app, paired, on a device the
  // owner configured to be a panel before this setting existed. Its behaviour is
  // the behaviour it has today — but see `backfillPanelDeviceConfiguration()`:
  // the next start records that as the explicit configuration.
  if (facts.nativeApp && facts.hasPanelCredential) {
    return { mode: 'takeover', source: 'legacy-native-paired' };
  }

  return { mode: 'app', source: 'default' };
}

// ─── This device ─────────────────────────────────────────────────────────────

function readUrlParamPanel(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('mode') === 'panel' || params.get('panel') === '1';
  } catch {
    return false;
  }
}

/** The facts about the running device, read once per call. */
export function readPanelDeviceFacts(storage: PanelDeviceStorage | null = safeStorage()): PanelDeviceFacts {
  return {
    configured: readPanelDeviceMode(storage),
    urlParamPanel: readUrlParamPanel(),
    nativeApp: isCapacitor(),
    hasPanelCredential: Boolean(readStoredPanelCredential(storage)),
  };
}

export function resolveThisDevicePanelMode(storage: PanelDeviceStorage | null = safeStorage()): PanelDeviceDecision {
  return resolvePanelDeviceMode(readPanelDeviceFacts(storage));
}

/** Is this device configured (or legacy-recognised) as a panel that takes the window over? */
export function isPanelDeviceTakeover(storage: PanelDeviceStorage | null = safeStorage()): boolean {
  return resolveThisDevicePanelMode(storage).mode === 'takeover';
}

/** Has the owner configured this device explicitly (i.e. is it not a default)? */
export function isPanelDeviceConfigured(storage: PanelDeviceStorage | null = safeStorage()): boolean {
  return readPanelDeviceMode(storage) !== null;
}

/**
 * Did the URL ask for a panel surface on a device that is NOT a takeover panel?
 * That is the one case that opens the miniapp by itself (a launcher shortcut /
 * a kiosk browser pointed at `?mode=panel` on a desktop install).
 */
export function shouldOpenPanelAppOnStart(storage: PanelDeviceStorage | null = safeStorage()): boolean {
  const decision = resolveThisDevicePanelMode(storage);
  return decision.mode === 'app' && decision.source === 'url';
}

/**
 * Record the legacy decision as an explicit configuration. Called from the one
 * place that acts on the mode (`stores/touchPanel.ts` on restore), so a paired
 * wall panel keeps working and stops depending on the legacy rule — and, after
 * this write, `isPanelDeviceTakeover()` is answering from configuration alone.
 */
export function backfillPanelDeviceConfiguration(
  storage: PanelDeviceStorage | null = safeStorage(),
): PanelDeviceMode | null {
  const decision = resolveThisDevicePanelMode(storage);
  if (decision.source !== 'legacy-native-paired') return null;
  writePanelDeviceMode(decision.mode, storage);
  return decision.mode;
}
